// TON transaction/block timestamps are Unix seconds. Treating that quantized
// value as the exact start of the second systematically adds 0..999ms to every
// visible age. Use the same conditional-midpoint estimator as the chain
// exporter: raw/2 while still inside the timestamp's partial first second,
// then raw-500ms. The estimate remains uncertain by up to +/-500ms, but it is
// unbiased and never manufactures a clipped pile of zeroes.

export function dequantizedChainAgeMs(unixSeconds: number, observedMs: number): number {
  if (!Number.isFinite(unixSeconds) || unixSeconds <= 0 || !Number.isFinite(observedMs)) return 0

  const rawMs = observedMs - unixSeconds * 1000
  if (rawMs <= 0) return 0

  const correctionWindowMs = Math.min(rawMs, 1000)
  return rawMs - correctionWindowMs / 2
}

export function dequantizedChainAgeSeconds(unixSeconds: number, observedMs: number): number {
  return Math.floor(dequantizedChainAgeMs(unixSeconds, observedMs) / 1000)
}

export function formatChainAge(unixSeconds: number, observedMs: number): string {
  const seconds = dequantizedChainAgeSeconds(unixSeconds, observedMs)
  if (seconds < 60) return `${seconds}s`
  const minutes = Math.floor(seconds / 60)
  if (minutes < 60) return `${minutes}m`
  const hours = Math.floor(minutes / 60)
  if (hours < 24) return `${hours}h`
  return `${Math.floor(hours / 24)}d`
}

// Portfolio Activity / last-active: two units, like `1h 11m` / `1m 17s`.
// Pair tape stays on formatChainAge — one coarsest unit.
export function formatPreciseChainAge(unixSeconds: number, observedMs: number): string {
  const seconds = dequantizedChainAgeSeconds(unixSeconds, observedMs)
  if (seconds < 60) return `${seconds}s`
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60)
    const rem = seconds % 60
    return rem > 0 ? `${minutes}m ${rem}s` : `${minutes}m`
  }
  if (seconds < 86400) {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`
  }
  const days = Math.floor(seconds / 86400)
  const hours = Math.floor((seconds % 86400) / 3600)
  return hours > 0 ? `${days}d ${hours}h` : `${days}d`
}
